import { Button, Form } from 'components';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useUpdateLocation } from 'api/locations/hooks';
import {
  buildSocialLink,
  getSocialInputValue,
  getSocialPrependText,
  SOCIALS_CONFIG,
} from 'constants/socialsConfig';
import validationSchemas from './schemas';

const LocationSocialsForm = ({ type = 'update', initialValues, onNext, onPrevious }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: updateLocation, isPending } = useUpdateLocation();

  const formInitialValues = useMemo(
    () => ({
      uid: initialValues?.uid || undefined,
      facebook: getSocialInputValue(initialValues.facebook, getSocialPrependText('facebook')),
      instagram: getSocialInputValue(initialValues.instagram, getSocialPrependText('instagram')),
      tiktok: getSocialInputValue(initialValues.tiktok, getSocialPrependText('tiktok')),
      x: getSocialInputValue(initialValues.x, getSocialPrependText('x')),
      pinterest: getSocialInputValue(initialValues.pinterest, getSocialPrependText('pinterest')),
    }),
    [initialValues],
  );
  const validationSchema = useMemo(() => validationSchemas.location_socials, []);

  const handleSubmit = useCallback(
    ({ uid, ...socials }) => {
      const socialLinks = {
        facebook: buildSocialLink(socials.facebook, getSocialPrependText('facebook')),
        instagram: buildSocialLink(socials.instagram, getSocialPrependText('instagram')),
        tiktok: buildSocialLink(socials.tiktok, getSocialPrependText('tiktok')),
        x: buildSocialLink(socials.x, getSocialPrependText('x')),
        pinterest: buildSocialLink(socials.pinterest, getSocialPrependText('pinterest')),
      };

      if (type === 'update') {
        updateLocation(
          { uid, content: socialLinks },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      } else if (onNext) {
        onNext();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateLocation, onNext, type],
  );

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      renderDefaultButtons={false}
    >
      {SOCIALS_CONFIG.map(({ name, label, bgColor, icon: Icon, prependText }) => (
        <Label key={name}>
          {label}
          <InputWithIconContainer>
            <IconContainer $bgColor={bgColor}>
              <Icon fill="#fff" />
            </IconContainer>
            <InputContainer>
              <Form.Input name={name} prepend={prependText} />
            </InputContainer>
          </InputWithIconContainer>
        </Label>
      ))}

      <ButtonsSection>
        {onPrevious && (
          <Button variant="secondary" block onClick={onPrevious}>
            {t('onboarding.previousStep')}
          </Button>
        )}
        <Form.Submit
          className="text-uppercase mt-0"
          size="lg"
          variant="primary"
          block
          isLoading={isPending}
          ignoreClickedState
        >
          {t('onboarding.nextStep')}
        </Form.Submit>
      </ButtonsSection>
    </Form>
  );
};

const Label = styled.label`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`;

const InputWithIconContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const IconContainer = styled.div`
  align-self: flex-start;
  flex-shrink: 0;
  width: 42px;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $bgColor }) => $bgColor || '#000'};
`;

const ButtonsSection = styled.div`
  width: 100%;
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

LocationSocialsForm.propTypes = {
  type: PropTypes.oneOf(['update']),
  initialValues: PropTypes.shape({
    uid: PropTypes.string,
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    tiktok: PropTypes.string,
    x: PropTypes.string,
    pinterest: PropTypes.string,
  }),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
};

export default LocationSocialsForm;
